import { useState, useEffect, useCallback, useRef } from 'react';
import { adminAPI } from '@/services/api';
import { EmailTemplate } from '@/types';

// 타입 정의
export interface TemplateListState {
  templates: EmailTemplate[];
  loading: boolean;
}

export interface TemplateEditorState {
  selected: string | null;
  content: string;
  preview: string;
}

export interface TemplateLoadingState {
  content: boolean;
  preview: boolean;
}

export interface TemplateRefs {
  editor: React.RefObject<HTMLDivElement>;
  isComposing: React.MutableRefObject<boolean>;
}

export interface TemplateHandlers {
  select: (templateId: string) => Promise<void>;
  save: () => Promise<string>;
  previewTemplate: () => Promise<void>;
  handleContentChange: (text: string) => void;
  applyHighlighting: (content: string, templateId: string) => void;
  loadTemplates: () => Promise<void>;
}

export interface UseEmailTemplatesReturn {
  list: TemplateListState;
  editor: TemplateEditorState;
  loadingState: TemplateLoadingState;
  error: string;
  setError: (error: string) => void;
  refs: TemplateRefs;
  handlers: TemplateHandlers;
}

export const useEmailTemplates = (): UseEmailTemplatesReturn => {
  // 목록 상태
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 에디터 상태
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [templateContent, setTemplateContent] = useState('');
  const [templatePreview, setTemplatePreview] = useState('');

  // 로딩 상태
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templatePreviewLoading, setTemplatePreviewLoading] = useState(false);

  // Refs
  const editorRef = useRef<HTMLDivElement>(null);
  const isComposingRef = useRef(false);
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const templatesData = await adminAPI.getEmailTemplates();
      setTemplates(templatesData);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail 
        || err.response?.data?.message 
        || err.message 
        || '이메일 템플릿 목록을 불러오는데 실패했습니다.';
      setError(errorMessage);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const getTextNodes = useCallback((node: Node): Text[] => {
    const textNodes: Text[] = [];
    const walker = document.createTreeWalker(
      node,
      NodeFilter.SHOW_TEXT,
      null
    );
    let textNode = walker.nextNode();
    while (textNode) {
      textNodes.push(textNode as Text);
      textNode = walker.nextNode();
    }
    return textNodes;
  }, []);

  const applyHighlighting = useCallback((content: string, templateId: string) => {
    if (!editorRef.current || !templateId) return;
    
    const currentTemplate = templates.find(t => t.id === templateId);
    const validVariables = currentTemplate?.variables || [];
    
    const escaped = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    const parts = escaped.split(/(\{\{[^}]+\}\})/);
    
    const highlighted = parts.map((part) => {
      const varMatch = part.match(/^\{\{([^}]+)\}\}$/);
      if (varMatch) {
        const varName = varMatch[1].trim();
        const isValid = validVariables.includes(varName);
        
        return `<span style="background-color: ${isValid ? '#f0f0f0' : '#fee2e2'}; border: 1px solid ${isValid ? '#d0d0d0' : '#ef4444'}; border-radius: 3px; padding: 2px 4px; color: ${isValid ? '#666' : '#dc2626'};">${part}</span>`;
      }
      return part;
    }).join('');
    
    const selection = window.getSelection();
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
    const cursorOffset = range ? range.startOffset : 0;
    
    editorRef.current.innerHTML = highlighted;
    
    if (range) {
      try {
        const newRange = document.createRange();
        const textNodes = getTextNodes(editorRef.current);
        if (textNodes.length > 0) {
          const firstNode = textNodes[0];
          const nodeLength = firstNode.textContent?.length || 0;
          if (cursorOffset <= nodeLength) {
            newRange.setStart(firstNode, Math.min(cursorOffset, nodeLength));
            newRange.collapse(true);
            selection?.removeAllRanges();
            selection?.addRange(newRange);
          }
        }
      } catch (e) {
        // 커서 복원 실패 시 무시
      }
    }
  }, [templates, getTextNodes]);

  const selectTemplate = useCallback(async (templateId: string) => {
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
      highlightTimeoutRef.current = null;
    }
    
    setSelectedTemplate(templateId);
    setTemplateLoading(true);
    setError('');
    try {
      const template = await adminAPI.getEmailTemplate(templateId);
      const content = template.content || '';
      setTemplateContent(content);
      setTemplatePreview('');
      
      setTimeout(() => {
        applyHighlighting(content, templateId);
      }, 0);
    } catch (err: any) {
      setError(err.response?.data?.detail || '템플릿을 불러오는데 실패했습니다.');
      setTemplateContent('');
      if (editorRef.current) {
        editorRef.current.innerHTML = '';
      }
    } finally {
      setTemplateLoading(false);
    }
  }, [applyHighlighting]);

  const saveTemplate = useCallback(async (): Promise<string> => {
    if (!selectedTemplate) {
      throw new Error('템플릿을 선택해주세요.');
    }
    
    setTemplateLoading(true);
    setError('');
    try {
      let contentToSave = templateContent;
      if (editorRef.current) {
        contentToSave = editorRef.current.textContent || templateContent;
        setTemplateContent(contentToSave);
      }
      await adminAPI.updateEmailTemplate(selectedTemplate, contentToSave);
      applyHighlighting(contentToSave, selectedTemplate);
      return '이메일 템플릿이 저장되었습니다.';
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || '이메일 템플릿 저장에 실패했습니다.';
      setError(errorMessage);
      throw err;
    } finally {
      setTemplateLoading(false);
    }
  }, [selectedTemplate, templateContent, applyHighlighting]);

  const previewTemplate = useCallback(async () => {
    if (!selectedTemplate) return;
    
    setTemplatePreviewLoading(true);
    setError('');
    try {
      const preview = await adminAPI.previewEmailTemplate(selectedTemplate);
      setTemplatePreview(preview.html);
    } catch (err: any) {
      setError(err.response?.data?.detail || '미리보기 생성에 실패했습니다.');
    } finally {
      setTemplatePreviewLoading(false);
    }
  }, [selectedTemplate]);

  const handleContentChange = useCallback((text: string) => {
    setTemplateContent(text);
    
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    
    const currentTemplateId = selectedTemplate;
    highlightTimeoutRef.current = setTimeout(() => {
      if (currentTemplateId) {
        applyHighlighting(text, currentTemplateId);
      }
    }, 500);
  }, [selectedTemplate, applyHighlighting]);

  return {
    // 목록 상태
    list: {
      templates,
      loading,
    },

    // 에디터 상태
    editor: {
      selected: selectedTemplate,
      content: templateContent,
      preview: templatePreview,
    },

    // 로딩 상태
    loadingState: {
      content: templateLoading,
      preview: templatePreviewLoading,
    },

    // 에러 상태
    error,
    setError,

    // Refs
    refs: {
      editor: editorRef,
      isComposing: isComposingRef,
    },

    // 핸들러
    handlers: {
      select: selectTemplate,
      save: saveTemplate,
      previewTemplate,
      handleContentChange,
      applyHighlighting,
      loadTemplates,
    },
  };
};

